import {
  IconAlertTriangle,
  IconCircleCheck,
  IconClock,
  IconFileInvoice,
  IconPackage,
} from "@tabler/icons-react";
import { Alert, Badge, Button, Card, Divider, Group, Heading, ListaItemsSkeleton, Skeleton, Stack, Text, ThemeIcon } from "@/components/ui";
import { useAuthStore } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";
import { usePolizas } from "../hooks/use-polizas";
import { useDescargarPolizasZip } from "../hooks/use-polizas-descargas";
import { PolizaItem } from "./PolizaItem";

const PERMISOS_DESCARGA_POLIZA = [
  PERMISOS.solicitudesVerTodas,
  PERMISOS.autorizacionAutorizar,
  PERMISOS.contabilizacionReintentar,
];

/**
 * Card que muestra todas las pólizas SAP de una solicitud (una por
 * CFDI · decisiones 47 y 59 v22). Polling 5 s para reaccionar al
 * depósito de SFTP que ocurre asíncrono.
 *
 * Si hay ≥2 depositadas, ofrece descarga conjunta como ZIP.
 * Retorna null cuando la lista está vacía · la card no se monta hasta
 * que el flujo de contabilización generó al menos una póliza.
 */
export function PolizasCard({ solicitudId }: { solicitudId: string }) {
  const { data, isPending, isError, error } = usePolizas(solicitudId, true);
  const descargarZip = useDescargarPolizasZip(solicitudId);

  if (isPending) return <PolizasCardSkeleton />;
  if (isError) {
    return <PlaceholderCard mensaje={`Error: ${(error as Error).message}`} esError />;
  }
  if (!data || data.length === 0) return null;

  // Hotspot 90 · descarga por PERMISO/ACCESO, no por rol. Si la lista cargó, el backend ya
  // validó el acceso a las pólizas de ESTA solicitud (AccesoPolizasDeSolicitud · dueño / ver
  // todas / autorizador de su organización). El TXT técnico se ofrece a quien consulta u opera
  // la contabilización (`solicitudes.ver_todas`, `autorizacion.autorizar`,
  // `contabilizacion.reintentar`); el dueño que solo captura sigue recibiendo únicamente la
  // confirmación del envío (P18).
  const permisos = useAuthStore.getState().user?.permisos ?? [];
  const puedeDescargar = PERMISOS_DESCARGA_POLIZA.some((p) => permisos.includes(p));

  const conError = data.filter((p) => p.estado === "ErrorEnDeposito");
  const depositadas = data.filter((p) => p.estado === "DepositadaEnSftp");
  const depositadasCount = depositadas.length;
  const todasDepositadas = depositadasCount === data.length;

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="flex-start" wrap="nowrap">
            <ThemeIcon size="lg" radius="md" variant="filled" color="multiva">
              <IconFileInvoice size={20} stroke={1.6} />
            </ThemeIcon>
            <div>
              <Group gap={8} align="baseline">
                <Heading order={5}>Pólizas SAP</Heading>
                <Badge color="multiva">{data.length}</Badge>
              </Group>
              <Text size="sm" c="dimmed" className="mt-0.5">
                Una póliza por CFDI · archivo TXT depositado en SFTP
                (decisiones 47, 59 v22).
              </Text>
            </div>
          </Group>
          {puedeDescargar && depositadasCount > 1 ? (
            <Button
              variant="default"
              size="xs"
              leftSection={<IconPackage size={12} />}
              loading={descargarZip.isPending}
              onClick={() => descargarZip.mutate()}
              title="Descarga todas las pólizas depositadas como un solo archivo ZIP"
            >
              Todas ({depositadasCount}) · ZIP
            </Button>
          ) : null}
        </Group>

        {conError.length > 0 ? (
          <Alert variant="error" icon={<IconAlertTriangle size={16} stroke={1.7} />} title="Error al enviar a contabilización">
            <Stack gap={4}>
              <Text size="sm">
                {conError.length === 1
                  ? "Ocurrió un error al enviar la póliza a contabilización:"
                  : `Ocurrió un error al enviar ${conError.length} pólizas a contabilización:`}
              </Text>
              {conError.map((p) => (
                <Text key={p.id} size="xs" c="peligro-800">
                  CFDI {p.numeroCfdi}: {p.errorDeposito ?? "Error desconocido."}
                </Text>
              ))}
            </Stack>
          </Alert>
        ) : todasDepositadas ? (
          <Alert variant="success" icon={<IconCircleCheck size={16} stroke={1.7} />}>
            {data.length === 1
              ? "La póliza se envió a contabilización correctamente."
              : `Las ${data.length} pólizas se enviaron a contabilización correctamente.`}
          </Alert>
        ) : (
          <Alert variant="info" icon={<IconClock size={16} stroke={1.7} />}>
            Enviando pólizas a contabilización… ({depositadasCount}/{data.length})
          </Alert>
        )}

        <Divider />

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((p) => (
            <PolizaItem key={p.id} poliza={p} solicitudId={solicitudId} puedeDescargar={puedeDescargar} />
          ))}
        </div>
      </Stack>
    </Card>
  );
}

function PlaceholderCard({ mensaje, esError = false }: { mensaje: string; esError?: boolean }) {
  return (
    <Card padding="lg">
      <Stack gap="xs">
        <Heading order={5}>Pólizas SAP</Heading>
        <Text size="sm" c={esError ? "peligro-700" : "dimmed"}>
          {mensaje}
        </Text>
      </Stack>
    </Card>
  );
}

/** Primera carga · encabezado de la tarjeta + 2 pólizas (mismo marco que la tarjeta real). */
function PolizasCardSkeleton() {
  return (
    <Card padding="lg">
      <Stack gap="md">
        <div className="flex items-start gap-3" aria-hidden="true">
          <Skeleton height={34} width={34} className="shrink-0" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton height={16} width={130} />
            <Skeleton height={12} width="60%" />
          </div>
        </div>
        <ListaItemsSkeleton items={2} className="gap-2" />
      </Stack>
    </Card>
  );
}
